import React from "react";

/**
 * TARU BrandLogo — uses the official uploaded TARU logo.
 * - Light backgrounds: shows the full logo (emblem + "TARU · We Shape Gold" wordmark) as-is.
 * - Dark backgrounds (sidebar, hero panels): shows the emblem + hand-set cream/gold wordmark
 *   (the source wordmark is dark green and would disappear on a dark backdrop).
 * - `showWordmark=false`: shows only the emblem (perfect for compact spaces / favicons).
 *
 * Assets live at /brand/ (public), so no auth or CORS concerns.
 */

const FULL_LOGO = "/brand/taru-logo-full.webp";
const EMBLEM = "/brand/taru-emblem.webp";

const GREEN = "#0F5E39";
const DEEP_GREEN = "#083F22";
const GOLD = "#C9A24B";
const CREAM = "#FAF7EF";

export function BrandLogo({
  size = 40,
  showWordmark = false,
  variant = "light",
  tagline = false,
  className = "",
  testId = "brand-logo",
}) {
  const emblem = (
    <img
      src={EMBLEM}
      alt=""
      width={size}
      height={size}
      style={{ width: size, height: size, display: "block" }}
      className="shrink-0"
    />
  );

  if (!showWordmark) {
    return (
      <div className={`inline-flex ${className}`} data-testid={testId} aria-label="STJ">
        {emblem}
      </div>
    );
  }

  if (variant === "dark") {
    // On dark backgrounds render emblem + custom cream/gold wordmark
    // (the logo image has a dark-green wordmark which would fade on dark bg).
    return (
      <div className={`inline-flex items-center gap-3 ${className}`} data-testid={testId}>
        {emblem}
        <div className="leading-none">
          <div
            style={{
              fontSize: Math.max(20, size * 0.62),
              fontFamily: "'Playfair Display', 'Cormorant Garamond', Georgia, serif",
              fontWeight: 700,
              color: CREAM,
              letterSpacing: "0.08em",
            }}
          >
            TARU
          </div>
          {tagline && (
            <div
              style={{
                fontSize: Math.max(10, size * 0.28),
                fontFamily: "'Playfair Display', 'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                color: GOLD,
                marginTop: 4,
                letterSpacing: "0.02em",
              }}
            >
              We Shape Gold
            </div>
          )}
        </div>
      </div>
    );
  }

  // Light variant: use full logo image (includes wordmark + tagline in native brand type)
  return (
    <div className={`inline-flex items-center ${className}`} data-testid={testId}>
      <img
        src={FULL_LOGO}
        alt="TARU · We Shape Gold"
        style={{ height: size * 1.18, width: "auto", display: "block" }}
      />
    </div>
  );
}

export const TARU_COLORS = { GREEN, DEEP_GREEN, GOLD, CREAM };
